/**
 * Die Auftragsakte — `PORTAL-002`, die acht Bereiche aus `07` §7.5.
 *
 * **Das ist die Klammer.** `07` §7.1 nennt sie als den einen Punkt, an dem
 * sich die Plattform von Microsoft 365 unterscheidet: Dort stehen dieselben
 * Bausteine nebeneinander, und die Zuordnung zum Auftrag leisten die Benutzer
 * von Hand. Hier erscheinen unter „Auftrag 2481" gemeinsam die Aufgaben, die
 * Dateien, die Dokumente, die Mails, der Chatraum und der Termin.
 *
 * **Nichts davon ist kopiert.** Die Akte hat nach `07` §7.5 eine stabile
 * Portal-ID und verknüpft ResourceRefs; die Inhalte bleiben in Vikunja,
 * Nextcloud, Paperless und Stalwart. Was diese Ansicht auflöst, ist der
 * Link-Graph aus `04` §4.3 — und deshalb steht in jedem Reiter eine Liste von
 * Verweisen, nicht ein eingebetteter Inhalt.
 *
 * Der Reiter „Verlauf" ist kein Bonus: `05` §5.15 stellt jeden Statuswechsel
 * unter Auditpflicht, und ADR-045 verlangt bei stellvertretendem Schalten
 * beide Namen. Eine Auditspur, die niemand ansehen kann, erfüllt die Auflage
 * auf dem Papier.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";

import {
  auftragNachNummer,
  berichteZuAuftrag,
  dauerMinuten,
  einsaetzeZuAuftrag,
  findeAblage,
  findeEinsatz,
  findeKunde,
  findePerson,
  nachbarn,
  summeMinuten,
  verknuepftMit,
  zeitenZuEinsatz,
} from "@hwc/demo-data";
import type { Ablageart, Bestand, Servicebericht } from "@hwc/demo-data";
import { hatQuellsystem } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";
import type { ResourceRef } from "@hwc/contracts";

import { Hinweis, Knopf, Zustand } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { Berichtsentscheidung, Einsatzsteuerung } from "./Steuerung.js";
import { Freigeben } from "./Freigeben.js";
import { Wiederherstellen } from "./Wiederherstellen.js";
import { schreibeBeitrag } from "../../src/daten/vorgaenge.js";
import {
  Einsatzabzeichen,
  Feld,
  Felder,
  Leer,
  Liste,
  Prioritaetsabzeichen,
  Reiter,
  Zeile,
  dauer,
  datum,
  groesse,
  uhrzeit,
  zeitfenster,
  zumKunden,
} from "../../src/bereiche/bausteine.js";

/** Die acht Bereiche aus `07` §7.5, in der Reihenfolge des Dokuments. */
const REITER = [
  { schluessel: "uebersicht", text: "Übersicht" },
  { schluessel: "einsaetze", text: "Einsätze und Aufgaben" },
  { schluessel: "dateien", text: "Dateien" },
  { schluessel: "dokumente", text: "Dokumente" },
  { schluessel: "kommunikation", text: "Kommunikation" },
  { schluessel: "termine", text: "Termine und Kontakte" },
  { schluessel: "wissen", text: "Wissen" },
  { schluessel: "verlauf", text: "Verlauf" },
] as const;

/** Die verknüpften Ablageobjekte einer Art, über den Link-Graphen aufgelöst. */
function ablageUeberGraph(
  bestand: Bestand,
  auftrag: ResourceRef,
  art: Ablageart,
) {
  return nachbarn(bestand, auftrag)
    .map((r) => findeAblage(bestand, r))
    .filter((o): o is NonNullable<typeof o> => o !== undefined && o.art === art);
}

export function Auftragsakte() {
  const { nummer } = useParams();
  const { bestand } = useSitzung();
  const [reiter, setzeReiter] = useState<string>("uebersicht");

  /**
   * Beim Wechsel auf einen anderen Auftrag zurück auf die Übersicht.
   *
   * Die Route bleibt dieselbe (`/auftraege/:nummer`), also bleibt auch die
   * Komponente montiert — und mit ihr der gewählte Reiter. Gemessen am
   * 05.09.2026: Wer im Verlauf von 2483 stand und über die Suche zu 2486
   * sprang, landete in dessen Verlauf. Das ist nicht falsch im Sinne von
   * kaputt, aber es beantwortet die falsche Frage: Wer einen Auftrag neu
   * öffnet, will wissen, worum es geht, nicht was zuletzt daran passiert ist.
   *
   * Kein `useEffect`, sondern der Vergleich beim Rendern — das ist das von
   * React empfohlene Muster für Zustand, der von einer Eigenschaft abhängt.
   * Ein Effekt würde einen zusätzlichen Durchlauf mit dem alten Reiter
   * zeichnen, und den sieht man als Aufblitzen.
   */
  const [zuletzt, setzeZuletzt] = useState(nummer);
  if (nummer !== zuletzt) {
    setzeZuletzt(nummer);
    setzeReiter("uebersicht");
  }

  const auftrag = nummer ? auftragNachNummer(bestand, nummer) : undefined;

  if (!auftrag) {
    return (
      <>
        <h1>Auftrag nicht gefunden</h1>
        <p className="leise">
          Zur Nummer <code>{nummer}</code> gibt es keinen Auftrag.{" "}
          <Link to="/auftraege">Zurück zur Liste</Link>.
        </p>
      </>
    );
  }

  const kunde = findeKunde(bestand, auftrag.kunde);
  const einsaetze = einsaetzeZuAuftrag(bestand, auftrag.ref);
  const berichte = berichteZuAuftrag(bestand, auftrag.ref);

  return (
    <>
      <div className="bereichskopf">
        <h1>
          {auftrag.nummer} — {auftrag.titel}
        </h1>
        <span className="bereichskopf__zusatz">
          {kunde && <Link to={zumKunden(kunde.nummer)}>{kunde.name}</Link>}
        </span>
      </div>

      <Reiter punkte={REITER} aktiv={reiter} beiWahl={setzeReiter} />

      {reiter === "uebersicht" && (
        <UebersichtsReiter auftrag={auftrag} einsaetze={einsaetze} berichte={berichte} />
      )}
      {reiter === "einsaetze" && <EinsatzReiter einsaetze={einsaetze} />}
      {reiter === "dateien" && (
        <>
          <AblageReiter auftrag={auftrag.ref} art="arbeitsdatei" />
          <Wiederherstellen auftrag={auftrag} />
          {/*
            Freigeben steht bei den Dateien und nicht in einem eigenen Reiter:
            Wer einen Zugang erstellt, hat gerade eine Datei vor sich — und ein
            Reiter „Freigaben" wäre der Ort, an dem man zuerst die Datei sucht.
          */}
          <Freigeben auftrag={auftrag} />
        </>
      )}
      {reiter === "dokumente" && <AblageReiter auftrag={auftrag.ref} art="dokument" />}
      {reiter === "kommunikation" && <KommunikationsReiter auftrag={auftrag.ref} />}
      {reiter === "termine" && <TerminReiter auftrag={auftrag.ref} />}
      {reiter === "wissen" && <AblageReiter auftrag={auftrag.ref} art="wissen" />}
      {reiter === "verlauf" && <VerlaufsReiter einsaetze={einsaetze} />}
    </>
  );
}

// ───────────────────────────── Übersicht ─────────────────────────────

function UebersichtsReiter({
  auftrag,
  einsaetze,
  berichte,
}: {
  auftrag: NonNullable<ReturnType<typeof auftragNachNummer>>;
  einsaetze: readonly NonNullable<ReturnType<typeof findeEinsatz>>[];
  berichte: readonly Servicebericht[];
}) {
  const { bestand } = useSitzung();
  const kunde = findeKunde(bestand, auftrag.kunde);

  const alleZeiten = einsaetze.flatMap((e) => zeitenZuEinsatz(bestand, e.ref));
  const materialposten = bestand.material.filter((m) =>
    einsaetze.some((e) => formatResourceRef(e.ref) === formatResourceRef(m.einsatz)),
  );

  const offen = berichte.find((b) => b.zustand === "zur_pruefung");
  const freigegeben = berichte.find((b) => b.zustand === "freigegeben");

  return (
    <>
      {offen && (
        <>
          <Hinweis titel="Ein Servicebericht wartet auf Ihre Prüfung.">
            Version {offen.version} zu „{findeEinsatz(bestand, offen.einsatz)?.titel}" wurde
            übermittelt. Freigabe oder Rückgabe entscheidet das Büro — der Monteur kann
            das nicht selbst.
          </Hinweis>
          <Berichtsentscheidung
            bericht={offen}
            einsatz={findeEinsatz(bestand, offen.einsatz)}
          />
        </>
      )}

      <Felder>
        <Feld name="Kunde">
          {kunde ? <Link to={zumKunden(kunde.nummer)}>{kunde.name}</Link> : "—"}
        </Feld>
        <Feld name="Status">{auftrag.status}</Feld>
        <Feld name="Verantwortlich">
          {findePerson(bestand, auftrag.verantwortlich)?.name ?? "—"}
        </Feld>
        <Feld name="Gewerk">{auftrag.gewerk ?? "—"}</Feld>
        <Feld name="Angelegt">{datum(auftrag.angelegt)}</Feld>
        <Feld name="Einsatzort">
          {auftrag.einsatzort
            ? `${auftrag.einsatzort.strasse}, ${auftrag.einsatzort.plz} ${auftrag.einsatzort.ort}`
            : kunde
              ? `${kunde.anschrift.strasse}, ${kunde.anschrift.plz} ${kunde.anschrift.ort}`
              : "—"}
        </Feld>
      </Felder>

      {auftrag.beschreibung && <p>{auftrag.beschreibung}</p>}

      <h2>Erfasste Zeit und Material</h2>
      <Felder>
        <Feld name="Gebuchte Zeit">{dauer(summeMinuten(alleZeiten))}</Feld>
        <Feld name="Davon abrechenbar">{dauer(summeMinuten(alleZeiten, true))}</Feld>
        <Feld name="Materialposten">{materialposten.length}</Feld>
      </Felder>
      {/*
        `01` §1.12 trennt Auftragszeit und Arbeitszeit scharf, und ADR-031 hält
        fest, dass die Plattform die gesetzliche Aufzeichnung **nicht** leistet.
        Der Satz steht hier und nicht nur im Dokument: Diese Summe ist der Ort,
        an dem jemand sie sonst dafür hielte.
      */}
      <p className="notiz">
        <strong>Auftragszeit, nicht Arbeitszeit.</strong> Diese Summen sind auf Einsätze
        gebucht und Grundlage der Abrechnung. Die gesetzliche Arbeitszeiterfassung —
        Beginn, Pause, Ende eines Arbeitstags — leistet die Plattform nach ADR-031 nicht.
      </p>

      {freigegeben && (
        <>
          <h2>Freigegeben, noch nicht veröffentlicht</h2>
          <Berichtsentscheidung
            bericht={freigegeben}
            einsatz={findeEinsatz(bestand, freigegeben.einsatz)}
          />
        </>
      )}

      <h2>Serviceberichte</h2>
      {berichte.length === 0 ? (
        <Leer text="Zu diesem Auftrag wurde noch kein Bericht erstellt." />
      ) : (
        <Liste>
          {berichte.map((b) => (
            <Zeile
              key={b.ref.opaqueId}
              titel={`Version ${b.version} — ${findeEinsatz(bestand, b.einsatz)?.titel ?? ""}`}
              unterzeile={
                <>
                  {findePerson(bestand, b.erstelltVon)?.name} · {datum(b.erstellt)}
                  {b.freigegebenAm
                    && ` · freigegeben von ${findePerson(bestand, b.freigegebenVon)?.name}`}
                </>
              }
              abzeichen={<BerichtsAbzeichen zustand={b.zustand} />}
              rechts={b.pdf ? "PDF vorhanden" : undefined}
            />
          ))}
        </Liste>
      )}
    </>
  );
}

function BerichtsAbzeichen({ zustand }: { zustand: Servicebericht["zustand"] }) {
  const text: Record<Servicebericht["zustand"], string> = {
    entwurf: "Entwurf",
    zur_pruefung: "Zur Prüfung",
    freigegeben: "Freigegeben",
    veroeffentlicht: "Veröffentlicht",
    ersetzt: "Ersetzt",
  };
  const ton: Record<Servicebericht["zustand"], string> = {
    entwurf: "neutral",
    zur_pruefung: "warnung",
    freigegeben: "erfolg",
    veroeffentlicht: "erfolg",
    ersetzt: "aus",
  };
  return <span className={`abzeichen abzeichen--${ton[zustand]}`}>{text[zustand]}</span>;
}

// ───────────────────────────── Einsätze ─────────────────────────────

function EinsatzReiter({
  einsaetze,
}: {
  einsaetze: readonly NonNullable<ReturnType<typeof findeEinsatz>>[];
}) {
  const { bestand } = useSitzung();

  if (einsaetze.length === 0) {
    return <Leer text="Für diesen Auftrag ist noch kein Einsatz geplant." />;
  }

  return (
    <>
      {einsaetze.map((e) => {
        const zeiten = zeitenZuEinsatz(bestand, e.ref);
        const material = bestand.material.filter(
          (m) => formatResourceRef(m.einsatz) === formatResourceRef(e.ref),
        );
        const vorgaenger = e.folgtAuf ? findeEinsatz(bestand, e.folgtAuf) : undefined;

        return (
          <section className="karte" key={e.ref.opaqueId}>
            <div className="bereichskopf">
              <h2 style={{ marginTop: 0 }}>{e.titel}</h2>
              <span className="bereichskopf__zusatz">
                <Prioritaetsabzeichen wert={e.prioritaet} />{" "}
                <Einsatzabzeichen zustand={e.zustand} />
              </span>
            </div>

            <Felder>
              <Feld name="Zeitfenster">
                {datum(e.von)}, {zeitfenster(e.von, e.bis)}
              </Feld>
              <Feld name="Monteur">{findePerson(bestand, e.monteur)?.name ?? "nicht zugewiesen"}</Feld>
              {e.tatsaechlichAngekommen && (
                <Feld name="Angekommen">{uhrzeit(e.tatsaechlichAngekommen)}</Feld>
              )}
              {e.tatsaechlichAbgeschlossen && (
                <Feld name="Abgeschlossen">{uhrzeit(e.tatsaechlichAbgeschlossen)}</Feld>
              )}
            </Felder>

            {e.wartetAuf && (
              <p className="notiz notiz--warnung">
                <strong>Wartet:</strong> {e.wartetAuf}
              </p>
            )}
            {e.hinweis && <p className="notiz">{e.hinweis}</p>}
            {vorgaenger && (
              <p className="notiz">
                <strong>Folgeeinsatz</strong> zu „{vorgaenger.titel}". Ein Einsatz bleibt
                genau eine Anfahrt (ADR-045) — Nacharbeit vor Ort ist ein neuer Einsatz,
                kein Rücksprung.
              </p>
            )}

            <h3>Arbeitsschritte</h3>
            {/*
              Eigene Unteraufgaben, keine Ankreuzkästchen im Beschreibungstext
              (ADR-015). Der Unterschied ist nicht kosmetisch: Nach `04` §4.12
              ändern zwei Monteure, die ohne Netz verschiedene Schritte abhaken,
              damit verschiedene Ressourcen — und es entsteht kein Konflikt.
            */}
            <Liste>
              {e.schritte.map((s) => (
                <Zeile
                  key={s.ref.opaqueId}
                  titel={
                    <>
                      {s.erledigt ? "✓ " : "○ "}
                      {s.text}
                    </>
                  }
                  unterzeile={
                    s.ausnahme
                      ? `Ausnahme: ${s.ausnahme}`
                      : s.pflicht
                        ? "Pflichtschritt"
                        : undefined
                  }
                  rechts={s.erledigtAm ? uhrzeit(s.erledigtAm) : undefined}
                />
              ))}
            </Liste>

            {zeiten.length > 0 && (
              <>
                <h3>Auftragszeit</h3>
                <Liste>
                  {zeiten.map((z) => (
                    <Zeile
                      key={z.ref.opaqueId}
                      titel={z.art}
                      unterzeile={
                        <>
                          {findePerson(bestand, z.monteur)?.name} ·{" "}
                          {zeitfenster(z.von, z.bis)}
                          {z.notiz ? ` · ${z.notiz}` : ""}
                        </>
                      }
                      abzeichen={
                        z.abrechenbar ? null : (
                          <span className="abzeichen abzeichen--neutral">
                            nicht abrechenbar
                          </span>
                        )
                      }
                      rechts={dauer(dauerMinuten(z.von, z.bis))}
                    />
                  ))}
                </Liste>
              </>
            )}

            {material.length > 0 && (
              <>
                <h3>Material</h3>
                <Liste>
                  {material.map((m) => (
                    <Zeile
                      key={m.ref.opaqueId}
                      titel={m.bezeichnung}
                      unterzeile={
                        <>
                          {m.artikelnummer ? `${m.artikelnummer} · ` : ""}
                          erfasst von {findePerson(bestand, m.erfasstVon)?.name}
                        </>
                      }
                      abzeichen={
                        m.abrechenbar ? null : (
                          <span className="abzeichen abzeichen--neutral">
                            nicht abrechenbar
                          </span>
                        )
                      }
                      rechts={`${m.menge} ${m.einheit}`}
                    />
                  ))}
                </Liste>
              </>
            )}

            <h3>Was das Büro hier tun kann</h3>
            <Einsatzsteuerung einsatz={e} />

            {e.zustand === "storniert" && zeiten.length > 0 && (
              <Zustand
                art="warnung"
                text="Storniert, aber es sind bereits Zeiten gebucht."
                kinder={
                  <>
                    ADR-045 nimmt diesen Fall in Kauf. Was bei der Übergabe an die
                    Branchensoftware mit solchen Posten geschieht, ist nach `09` §9.3
                    eine offene Frage an `EXP-001`.
                  </>
                }
              />
            )}
          </section>
        );
      })}
    </>
  );
}

// ───────────────────────── Dateien, Dokumente, Wissen ─────────────────────────

const ARTTEXT: Record<Ablageart, { titel: string; leer: string; quelle: string }> = {
  arbeitsdatei: {
    titel: "Arbeitsdateien",
    leer: "Zu diesem Auftrag liegen keine Dateien.",
    quelle: "Nextcloud",
  },
  dokument: {
    titel: "Archivierte Dokumente",
    leer: "Zu diesem Auftrag ist nichts archiviert.",
    quelle: "Paperless",
  },
  wissen: {
    titel: "Wissen",
    leer: "Zu diesem Auftrag ist keine Wissensseite verknüpft.",
    quelle: "Collectives",
  },
};

function AblageReiter({ auftrag, art }: { auftrag: ResourceRef; art: Ablageart }) {
  const { bestand } = useSitzung();
  const objekte = useMemo(
    () => ablageUeberGraph(bestand, auftrag, art),
    [bestand, auftrag, art],
  );
  const beschriftung = ARTTEXT[art];

  return (
    <>
      {objekte.length === 0 ? (
        <Leer text={beschriftung.leer} />
      ) : (
        <Liste>
          {objekte.map((o) => (
            <Zeile
              key={o.ref.opaqueId}
              /*
               * **Der Weg ins Quellsystem — gegen eine Attrappe.**
               *
               * `07` §7.12: „Der primäre Zugriff erfolgt über den Auftrag."
               * Genau das ist diese Zeile: Man kommt an die Datei, weil man
               * den Auftrag offen hat, nicht weil man den Pfad kennt.
               *
               * Was dahinter liegt, ist bis `LNK-001` eine Attrappe, und die
               * Zielansicht sagt das auch. Der Weg selbst ist trotzdem echt —
               * Rahmen, Fokusführung, Rückweg und die Absage eines
               * Quellsystems, das nicht ausliefert, sind dieselben.
               */
              zu={hatQuellsystem(o.ref) ? `/oeffnen/${o.ref.opaqueId}` : undefined}
              titel={o.name}
              unterzeile={
                <>
                  {o.pfad ? `${o.pfad} · ` : ""}
                  geändert {datum(o.geaendert)}
                  {o.geaendertVon ? ` von ${o.geaendertVon}` : ""}
                </>
              }
              abzeichen={
                o.offline === "nur-online" ? (
                  <span className="abzeichen abzeichen--neutral">nur online</span>
                ) : null
              }
              rechts={groesse(o.groesseBytes)}
            />
          ))}
        </Liste>
      )}
      {/*
        `07` §7.12: „Technische Herkunft wird als sekundärer Hinweis angezeigt.
        Der primäre Zugriff erfolgt über den Auftrag." Deshalb steht der
        Systemname unter der Liste und nicht in jeder Zeile.

        **Der zweite Absatz sagt, was hier noch nicht geht.**

        Vorher stand hier nur „beim Öffnen prüft das Quellsystem die
        Berechtigung erneut" — im Präsens, obwohl sich nichts öffnen lässt.
        Beim Durchklicken liest sich das wie eine vorhandene Funktion, und wer
        den Öffnen-Knopf sucht, sucht den Fehler bei sich statt im Backlog.

        `PORTAL-002` verlangt genau das Gegenteil: „zeigt die Akte Verweise
        ohne Ziel — **sichtbar gekennzeichnet**, statt in tote Links zu
        führen." Tote Links gibt es keine; die Kennzeichnung fehlte.
      */}
      <p className="notiz">
        Führendes System: <strong>{beschriftung.quelle}</strong>. Die Plattform hält
        keine Kopie — beim Öffnen prüft das Quellsystem die Berechtigung erneut
        (`01` §1.11).
      </p>
      <p className="notiz notiz--warnung" data-hinweis="kein-ziel">
        <strong>Öffnen führt zu einer Attrappe.</strong> Der Sprung selbst ist
        echt — Rahmen, Rückweg und die Absage eines Quellsystems, das nicht
        ausliefert. Was fehlt, ist der Dienst dahinter: Der braucht die
        Verweisregistry aus <code>LNK-001</code> und den Adapter zu{" "}
        {beschriftung.quelle}. <strong>Hochladen und Ändern gibt es hier nicht</strong>,
        auch nicht gegen eine Attrappe.
      </p>
    </>
  );
}

// ───────────────────────── Kommunikation ─────────────────────────

function KommunikationsReiter({ auftrag }: { auftrag: ResourceRef }) {
  const { bestand } = useSitzung();

  const verknuepft = new Set(
    nachbarn(bestand, auftrag).map((r) => formatResourceRef(r)),
  );

  const mails = bestand.nachrichten.filter((n) => verknuepft.has(formatResourceRef(n.ref)));
  const raum = bestand.chatraeume.find(
    (c) => formatResourceRef(c.auftrag) === formatResourceRef(auftrag),
  );
  const beitraege = raum
    ? bestand.chatbeitraege.filter(
        (b) => formatResourceRef(b.raum) === formatResourceRef(raum.ref),
      )
    : [];

  return (
    <>
      <h2 style={{ marginTop: 0 }}>E-Mail</h2>
      {mails.length === 0 ? (
        <Leer text="Diesem Auftrag ist keine Nachricht zugeordnet." />
      ) : (
        <Liste>
          {mails.map((n) => (
            <Zeile
              key={n.ref.opaqueId}
              /*
               * Zum Lesen in **Bulwark**, nicht in Stalwart: Die Mail liegt
               * dort, aber die Oberfläche gehört dem Client (ADR-019). Das
               * Portal zeigt die Spur und führt hinüber — den Volltext rendert
               * es nach `07` §7.10 bewusst nicht, sonst wäre es der zweite
               * Mailclient, den die Plattform gerade vermeiden soll.
               */
              zu={`/oeffnen/${n.ref.opaqueId}`}
              titel={n.betreff}
              unterzeile={
                <>
                  {n.richtung === "eingehend" ? "von" : "an"}{" "}
                  {n.richtung === "eingehend" ? n.von : n.an.join(", ")} ·{" "}
                  {datum(n.gesendet)} {uhrzeit(n.gesendet)}
                </>
              }
              abzeichen={
                n.gelesen ? null : (
                  <span className="abzeichen abzeichen--warnung">ungelesen</span>
                )
              }
              rechts={n.anhaenge.length > 0 ? `${n.anhaenge.length} Anhang` : undefined}
            />
          ))}
        </Liste>
      )}
      {/*
        `07` §7.10: E-Mail bleibt Bürofunktion, und das Büro arbeitet in
        Bulwark. Was hier steht, ist die Spur — nicht der Mailclient. Ein
        Portal, das den Volltext rendert, wäre der zweite Client, den die
        Plattform gerade vermeiden soll.
      */}
      <p className="notiz">
        Verfasst und beantwortet wird in Bulwark (ADR-019). Das Portal zeigt die Spur
        und führt sie mit Chat und Dokumenten zusammen; ein Klick führt hinüber —
        bis <code>MAIL-004</code> zu einer Attrappe.
      </p>

      <h2>Bürochat</h2>
      {!raum ? (
        <Leer text="Für diesen Auftrag gibt es keinen Talk-Raum. Nicht jeder braucht einen." />
      ) : beitraege.length === 0 ? (
        <Leer text="Der Raum besteht, aber es wurde noch nichts geschrieben." />
      ) : (
        <Liste>
          {beitraege.map((b) => (
            <Zeile
              key={b.ref.opaqueId}
              titel={findePerson(bestand, b.autor)?.name ?? "unbekannt"}
              unterzeile={b.text}
              abzeichen={
                b.anhang ? (
                  <span className="abzeichen abzeichen--neutral">
                    {findeAblage(bestand, b.anhang)?.name ?? "Anhang"}
                  </span>
                ) : null
              }
              rechts={`${datum(b.gesendet)} ${uhrzeit(b.gesendet)}`}
            />
          ))}
        </Liste>
      )}
      {raum && <Chatfeld raum={raum.ref} />}

      <p className="notiz">
        Textchat, Bilder, Sprachnachrichten und Dateiverweise. Anrufe und
        Videokonferenz sind nach ADR-018 nicht Bestandteil von Version 1 — für Sprache
        bleibt der Telefonanruf.
      </p>
    </>
  );
}

// ───────────────────────── Termine und Kontakte ─────────────────────────

function TerminReiter({ auftrag }: { auftrag: ResourceRef }) {
  const { bestand } = useSitzung();

  const termine = bestand.termine.filter(
    (t) => t.auftrag && formatResourceRef(t.auftrag) === formatResourceRef(auftrag),
  );
  const auftragsObjekt = bestand.auftraege.find(
    (a) => formatResourceRef(a.ref) === formatResourceRef(auftrag),
  );
  const kunde = auftragsObjekt ? findeKunde(bestand, auftragsObjekt.kunde) : undefined;

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Termine</h2>
      {termine.length === 0 ? (
        <Leer text="Zu diesem Auftrag ist kein Termin eingetragen." />
      ) : (
        <Liste>
          {termine.map((t) => (
            <Zeile
              key={t.ref.opaqueId}
              titel={t.titel}
              unterzeile={
                <>
                  {t.ort ?? ""} ·{" "}
                  {t.teilnehmer
                    .map((r) => findePerson(bestand, r)?.name ?? "")
                    .filter(Boolean)
                    .join(", ")}
                </>
              }
              rechts={`${datum(t.von)} ${zeitfenster(t.von, t.bis)}`}
            />
          ))}
        </Liste>
      )}

      <h2>Kontakte</h2>
      {!kunde || kunde.kontakte.length === 0 ? (
        <Leer text="Für diesen Kunden ist kein Ansprechpartner hinterlegt." />
      ) : (
        <Liste>
          {kunde.kontakte.map((k) => (
            <Zeile
              key={k.name}
              titel={k.name}
              unterzeile={
                <>
                  {k.funktion ?? ""}
                  {k.telefon ? ` · ${k.telefon}` : ""}
                  {k.email ? ` · ${k.email}` : ""}
                </>
              }
              abzeichen={
                k.fuerMonteur ? null : (
                  <span className="abzeichen abzeichen--neutral">nicht für Monteure</span>
                )
              }
              rechts={
                k.telefon ? (
                  // `07` §7.7 nennt den `tel:`-Link ausdrücklich. Er ist die
                  // Antwort auf ADR-018: Anrufe gibt es nicht in der Anwendung,
                  // der normale Telefonanruf bleibt.
                  <a href={`tel:${k.telefon.replace(/\s/g, "")}`}>anrufen</a>
                ) : undefined
              }
            />
          ))}
        </Liste>
      )}
      <p className="notiz">
        Kontakte ohne Freigabe für Monteure werden in der Monteur-App nicht vorgeladen
        (`07` §7.9). Die Durchwahl der Buchhaltung gehört nicht auf jedes Monteurhandy.
      </p>
    </>
  );
}

// ───────────────────────────── Verlauf ─────────────────────────────

function VerlaufsReiter({
  einsaetze,
}: {
  einsaetze: readonly NonNullable<ReturnType<typeof findeEinsatz>>[];
}) {
  const { bestand } = useSitzung();

  const kennungen = new Set(einsaetze.map((e) => formatResourceRef(e.ref)));
  const eintraege = bestand.audit
    .filter((a) => kennungen.has(formatResourceRef(a.objekt)))
    .slice()
    .sort((x, y) => y.zeitpunkt.localeCompare(x.zeitpunkt));

  const kanten = einsaetze.flatMap((e) => verknuepftMit(bestand, e.ref));

  return (
    <>
      {eintraege.length === 0 ? (
        <Leer text="Zu diesem Auftrag ist noch nichts protokolliert." />
      ) : (
        <Liste>
          {eintraege.map((a) => (
            <Zeile
              key={a.ref.opaqueId}
              titel={a.handlung}
              unterzeile={
                <>
                  {findePerson(bestand, a.akteur)?.name ?? a.akteur.opaqueId}
                  {/*
                    ADR-045: Bei stellvertretendem Schalten protokolliert das
                    Audit **beide** — den Schaltenden und den Zugewiesenen.
                    Deshalb steht der zweite Name hier und nicht nur im
                    Datensatz.
                  */}
                  {a.imAuftragVon && (
                    <> im Auftrag von {findePerson(bestand, a.imAuftragVon)?.name}</>
                  )}
                  {a.von && a.nach && (
                    <>
                      {" "}
                      · {a.von} → {a.nach}
                    </>
                  )}
                </>
              }
              rechts={`${datum(a.zeitpunkt)} ${uhrzeit(a.zeitpunkt)}`}
            />
          ))}
        </Liste>
      )}

      <h2>Verknüpfungen</h2>
      <p className="leise">
        Die Kanten des Link-Graphen (`04` §4.3). Sie tragen keine Inhalte, nur
        typisierte Beziehungen zwischen ResourceRefs.
      </p>
      {kanten.length === 0 ? (
        <Leer text="Keine Verknüpfungen." />
      ) : (
        <Liste>
          {kanten.map((k, i) => (
            <Zeile
              key={`${k.relation}-${k.source.opaqueId}-${k.target.opaqueId}-${i}`}
              titel={k.relation}
              unterzeile={
                <code style={{ fontSize: "0.85em" }}>
                  {formatResourceRef(k.source)} → {formatResourceRef(k.target)}
                </code>
              }
              rechts={datum(k.createdAt)}
            />
          ))}
        </Liste>
      )}
    </>
  );
}

/**
 * Das Eingabefeld des Auftragsraums — `07` §7.10.
 *
 * **Warum der Chat überhaupt im Portal steht**, obwohl E-Mail in Bulwark
 * bleibt: Der Talk-Raum gehört zum Auftrag, nicht zum Postfach. `07` §7.5
 * führt die Kommunikationsspur als einen der acht Bereiche der Akte, und wer
 * einen Auftrag offen hat, soll dem Monteur schreiben können, ohne die
 * Anwendung zu wechseln.
 *
 * **Absenden mit Strg+Enter, nicht mit Enter.** In einem mehrzeiligen Feld ist
 * Enter ein Zeilenumbruch; wer es zum Senden umdeutet, verschickt halbe Sätze.
 * Der Knopf steht trotzdem daneben — `07` §7.14 verlangt, dass wichtige
 * Aktionen nicht in Tastenkürzeln versteckt sind.
 */
function Chatfeld({ raum }: { raum: ResourceRef }) {
  const { person, fuehreAus } = useSitzung();
  const [text, setzeText] = useState("");
  const [absage, setzeAbsage] = useState<string | null>(null);

  const senden = () => {
    const grund = fuehreAus((v) =>
      schreibeBeitrag({ vorgaenge: v, raum, autor: person.ref, text }),
    );
    setzeAbsage(grund ?? null);
    if (!grund) setzeText("");
  };

  return (
    <div className="chatfeld">
      {absage && <Zustand art="warnung" text={absage} />}
      <textarea
        className="chatfeld__eingabe"
        rows={3}
        value={text}
        placeholder="An den Auftragsraum schreiben — Strg+Enter sendet"
        onChange={(e) => setzeText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            senden();
          }
        }}
      />
      <div className="filter">
        <Knopf type="button" art="haupt" disabled={!text.trim()} onClick={senden}>
          Senden
        </Knopf>
      </div>
      <p className="notiz">
        {/*
          Was hier fehlt, und warum. Ein Knopf „Bild" ohne Dateiwahl wäre die
          schlechtere Ehrlichkeit.
        */}
        Bilder, Sprachnachrichten und Dateiverweise aus Nextcloud brauchen den Adapter
        aus <code>NC-002</code>. Der Raum selbst liegt in Nextcloud Talk; das Portal
        stellt ihn in der Akte dar.
      </p>
    </div>
  );
}
